import {
  accountService,
  locationService,
  membershipService,
  shiftService,
  weeklyScheduleService,
  labelsService,
  contractService,
  queryClientOptions,
  scheduleMembershipService
} from '@libs/data-access/queries'
import { QueriesRequestsStatus } from '@spa/features/Queries'
import moment from 'moment'
import React from 'react'
import type { FC } from 'react'

import { ShiftForm } from './ShiftForm'

type Props = {
  onFormSuccess: () => void
  date?: string | Date
  shiftId?: number
  userContractId?: number
  weeklyscheduleId: number
  labelId?: number
}

/**
 * Container that will load all datas needed by the ShiftForm
 * Might be temporary, but for the switch from redux to RQ, it helps by having clean and separated layers data/components
 */
export const ShiftFormContainer: FC<Props> = ({
  onFormSuccess,
  date,
  shiftId,
  userContractId,
  weeklyscheduleId,
  labelId
}) => {
  // Load current account
  const accountQuery = accountService.useFetchCurrent(
    {},
    { staleTime: queryClientOptions.time.STATIC }
  )

  // Load current shift if any is given
  const shiftQuery = shiftService.api.useFetchShift(
    {
      pathParams: { id: shiftId }
    },
    { enabled: !!shiftId }
  )

  // Load currentMebership to see if we can display some actions reserved to manager and director
  const currentUserMembershipQuery =
    membershipService.membership.useFetchCurrent()

  // Load contract of the employee in the shift
  const shiftContractQuery = contractService.contract.useFetchOne(
    {
      pathParams: {
        user_contract_id: shiftQuery.data?.user_contract_id ?? 0
      }
    },
    {
      enabled: Boolean(shiftQuery.data?.user_contract_id),
      staleTime: queryClientOptions.time.STATIC
    }
  )

  // Load membership of the employee in the shift (only used in edition)
  const shiftMembershipQuery =
    scheduleMembershipService.membership.useFetchScheduleMembership(
      {
        pathParams: {
          membership_id: shiftContractQuery.data?.membership_id ?? 0
        }
      },
      {
        enabled: Boolean(shiftContractQuery.data?.membership_id),
        staleTime: queryClientOptions.time.STATIC
      }
    )
  /**
   * All datas ugly section...
   * Not optimised at all, but for now it will do
   * Will be improved after the complete removal of redux from all planning modals
   */
  // Load all locations of the account. It includes all the teams
  const plannableLocationsQuery = locationService.location.useFetchAll(
    {},
    { staleTime: queryClientOptions.time.STATIC }
  )

  // Load all Labels of all teams of the account
  const labelsQuery = labelsService.api.useFetchAll({})

  // Load all teams
  const accountTeams =
    plannableLocationsQuery.data?.map(location => location.teams).flat() ?? []

  // Load all weeklySchedule in the same week as the current one
  const plannableWeeklySchedulesQuery = weeklyScheduleService.api.useFetchAll(
    {
      pathParams: {
        accountId: accountQuery.data?.id
      },
      queryParams: {
        week: moment(date ?? shiftQuery.data?.starts_at).format('GGGG-[W]WW'),
        with_permissions: true
      }
    },
    {
      enabled:
        !!accountQuery.data?.id && (!!date || !!shiftQuery.data?.starts_at),
      staleTime: queryClientOptions.time.STATIC
    }
  )

  // Load current weeklySchedule
  const currentWeeklySchedule = plannableWeeklySchedulesQuery.data?.find(
    weeklyS => weeklyS.id === weeklyscheduleId
  )

  // Search and find current weeklySchedule location
  const currentWeeklyScheduleLocation =
    plannableLocationsQuery.data?.filter(
      location =>
        location.teams.filter(
          team => team.id === currentWeeklySchedule?.team_id
        ).length > 0
    )[0] ?? undefined

  /**
   * Datas from shift
   */
  // Load teamId from the shift
  const weeklyscheduleFromShiftTeamId =
    plannableWeeklySchedulesQuery.data?.filter(
      fWks => fWks?.id === shiftQuery.data?.weeklyschedule_id
    )[0]?.team_id ?? undefined

  // Load team
  const currentShiftTeam = accountTeams.find(
    team => team.id === weeklyscheduleFromShiftTeamId
  )

  const handleFomSuccess = () => {
    if (onFormSuccess) {
      onFormSuccess()
    }
  }

  const mandatoryData =
    accountQuery.data &&
    plannableWeeklySchedulesQuery.data &&
    currentWeeklySchedule &&
    currentWeeklyScheduleLocation &&
    plannableLocationsQuery.data &&
    currentUserMembershipQuery.data &&
    accountTeams &&
    labelsQuery.data &&
    ((shiftId && shiftQuery.data) || !shiftId)
  const dataMembership =
    shiftId && shiftQuery.data?.user_contract_id
      ? shiftMembershipQuery.data && shiftContractQuery.data
      : true

  if (!mandatoryData || !dataMembership) {
    return (
      <QueriesRequestsStatus
        className='m-4'
        statuses={[
          accountQuery.status,
          shiftContractQuery.status,
          shiftQuery.status,
          plannableLocationsQuery.status,
          labelsQuery.status,
          plannableWeeklySchedulesQuery.status,
          shiftMembershipQuery.status,
          currentUserMembershipQuery.status
        ]}
        errors={[
          accountQuery.error,
          shiftContractQuery.error,
          shiftQuery.error,
          plannableLocationsQuery.error,
          labelsQuery.error,
          plannableWeeklySchedulesQuery.error,
          shiftMembershipQuery.error,
          currentUserMembershipQuery.error
        ]}
      />
    )
  }

  return (
    <ShiftForm
      onFormSuccess={handleFomSuccess}
      labelId={labelId}
      date={date}
      userContractId={userContractId}
      currentAccount={accountQuery.data}
      currentUserMembership={currentUserMembershipQuery.data}
      weeklySchedules={plannableWeeklySchedulesQuery.data}
      currentWeeklyScheduleLocation={currentWeeklyScheduleLocation}
      currentShift={shiftQuery.data}
      currentShiftMembership={shiftMembershipQuery.data}
      currentWeeklySchedule={currentWeeklySchedule}
      currentShiftTeam={currentShiftTeam}
      allAccountLocations={plannableLocationsQuery.data}
      allAccountTeams={accountTeams}
      allLabels={labelsQuery.data}
    />
  )
}
